import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { useAuthStore, initAuthListener } from '@/store/authStore';
import { MainLayout } from '@/components/layout/MainLayout';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { ProtectedRoute } from '@/components/auth/Protectedroute';
import AuthLayout from '@/components/auth/AuthLayout';

// Lazy load pages for code splitting
// Public pages
const LandingPage = lazy(() => import('@/pages/Landing/LandingPage'));

// Auth pages (these are now child routes of AuthLayout)
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const OAuthSigninPage = lazy(() => import('@/pages/auth/OAuthSigninPage'));
const JoinDeepLinkPage = lazy(() => import('@/pages/groups/JoinDeepLinkPage'));

// Protected pages
const HomePage = lazy(() => import('@/pages/home/HomePage'));
const ExplorePage = lazy(() => import('@/pages/explore/ExplorePage'));
const AskAIPage = lazy(() => import('@/pages/ai/AskAiPage'));
const GroupsPage = lazy(() => import('@/pages/groups/GroupsPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const EditProfilePage = lazy(() => import('@/pages/profile/EditProfilePage'));
const DecisionDetailPage = lazy(() => import('@/pages/decision/DecisionDetailPage'));
const CreateDecisionPage = lazy(() => import('@/pages/decision/CreateDecisionPage'));
const CreatePollPage = lazy(() => import('@/pages/poll/CreatePollPage'));
const PollDetailPage = lazy(() => import('@/pages/poll/PollDetailPage'));
const GroupDetailPage = lazy(() => import('@/pages/groups/GroupDetailPage'));
const CreateGroupPage = lazy(() => import('@/pages/groups/CreateGroupPage'));
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const DeleteAccountPage = lazy(() => import('@/pages/settings/DeleteAccountPage'));
const SearchPage = lazy(() => import('@/pages/search/SearchPage'));
const CategoryPage = lazy(() => import('@/pages/category/CategoryPage'));
const LeaderboardPage = lazy(() => import('@/pages/leaderboard/LeaderboardPage'));
const BookmarksPage = lazy(() => import('@/pages/bookmarks/BookmarksPage'));
const NotFoundPage = lazy(() => import('@/pages/error/NotFoundPage'));
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy/PrivacyPolicyPage'));
const TermsAndConditionsPage = lazy(() => import('@/pages/terms/TermsAndConditionsPage'));
const CsaePolicyPage = lazy(() => import('@/pages/privacy/CsaePolicyPage'));
const ChangePasswordPage = lazy(() => import('./pages/settings/ChangePasswordPage'));
// eslint-disable-next-line @typescript-eslint/no-unused-vars
//const SearchResultsPage = lazy(() => import('./pages/search/SearchPage'));
const LinkedAccountsPage = lazy(() => import('./pages/settings/LinkedAccountsPage'));
const BlockedUsersPage = lazy(() => import('./pages/settings/BlockedUsersPage'));

function App() {
  useEffect(() => {
    // Syncs the auth store with the Supabase session lifecycle (expiry,
    // sign-out in other tabs, token refresh, stale persisted state).
    initAuthListener();
  }, []);

  // Landing wrapper: if user already authenticated, send to /home
  const LandingWrapper = () => {
    const { isAuthenticated } = useAuthStore();
    if (isAuthenticated) return <Navigate to="/home" replace />;
    return <LandingPage />;
  };
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* ============================================
            PUBLIC ROUTES - No authentication required
            ============================================ */}

        {/* Landing Page - Main entry point for visitors */}
        <Route path="/" element={<LandingWrapper />} />
        {/* deep link route accessible without login */}
        <Route path="/invite/accept" element={<JoinDeepLinkPage />} />

        {/* ============================================
            AUTH ROUTES - Nested under AuthLayout
            LeftPanel stays static, only form content changes
            ============================================ */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* OAuth callback */}
        <Route path="/oauth-signin" element={<OAuthSigninPage />} />

        {/* ============================================
            PROTECTED ROUTES - Requires authentication
            ============================================ */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          {/* Main App - /home is the authenticated home feed */}
          <Route path="/home" element={<HomePage />} />
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/ask-decixa" element={<AskAIPage />} />

          {/* Groups */}
          <Route path="/groups" element={<GroupsPage />} />
          <Route path="/groups/:groupId" element={<GroupDetailPage />} />
          <Route path="/groups/create" element={<CreateGroupPage />} />

          {/* Profile */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/edit" element={<EditProfilePage />} />
          <Route path="/profile/:userId" element={<ProfilePage />} />

          {/* Decisions */}
          <Route path="/decision/:decisionId" element={<DecisionDetailPage />} />
          <Route path="/create" element={<CreateDecisionPage />} />

          {/* Polls */}
          <Route path="/poll/:pollId" element={<PollDetailPage />} />
          <Route path="/create-poll" element={<CreatePollPage />} />

          {/* Other Pages */}
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/category/:categorySlug" element={<CategoryPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/bookmarks" element={<BookmarksPage />} />
          <Route path="/settings/password" element={<ChangePasswordPage />} />
          <Route path="/settings/linked-accounts" element={<LinkedAccountsPage />} />
          <Route path="/settings/blocked" element={<BlockedUsersPage />} />
        </Route>

        {/* ============================================
            PUBLIC ROUTES - misc pages (privacy, etc.)
            ============================================ */}
        <Route path="/csae-policy" element={<CsaePolicyPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />

        {/* ============================================
            404 - Page Not Found
            ============================================ */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;